import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingBag, Store, Bike, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Input } from "../components/ui/input";
import ShopAddressFields from "../components/ShopAddressFields";
import BankFields from "../components/BankFields";
import RiderKycFields, { riderErrors } from "../components/RiderKycFields";
import PhoneOtp from "../components/PhoneOtp";
import EmailCode from "../components/EmailCode";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-end p-12 hero-radial relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1498049794561-7780e7231661?w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative space-y-3">
          <p className="font-display text-5xl font-black leading-tight">Everything.<br /><span className="text-[#ff5500]">Delivered.</span></p>
          <p className="text-slate-300 max-w-sm">Join EAZYSHOPY for exclusive deals, fast delivery and 3D product previews.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6 rise">
          <div><h1 className="text-3xl font-black tracking-tight">{title}</h1><p className="text-slate-400 text-sm mt-1">{sub}</p></div>
          {children}
        </div>
      </div>
    </div>
  );
}

const btn = "btn-glow w-full py-3 rounded-full bg-[#ff5500] hover:bg-[#ff6b1a] font-semibold transition-colors disabled:opacity-50";
const Err = ({ id, msg }) => (msg ? <p data-testid={id} className="text-xs text-red-400 mt-1 pl-1">{msg}</p> : null);

export function PasswordInput({ id, value, onChange, placeholder = "Password", autoComplete, name }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input data-testid={id} type={show ? "text" : "password"} name={name} autoComplete={autoComplete} placeholder={placeholder} value={value} onChange={onChange} className="h-12 pr-12" />
      <button type="button" data-testid={`${id}-toggle`} onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
    </div>
  );
}

export function strength(pw) {
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}
const LEVELS = [["Too short", "bg-red-500"], ["Weak", "bg-red-500"], ["Okay", "bg-amber-400"], ["Good", "bg-emerald-400"], ["Strong", "bg-emerald-500"]];

const hint = (pw, s) => {
  if (s >= 3 || pw.length < 6) return "";
  const miss = [!/\d/.test(pw) && "a number", !(/[A-Z]/.test(pw) && /[a-z]/.test(pw)) && "upper & lower case", !/[^A-Za-z0-9]/.test(pw) && "a symbol", pw.length < 10 && "more characters"].filter(Boolean);
  return miss.length ? ` · try adding ${miss.slice(0, 2).join(" or ")}` : "";
};

function StrengthMeter({ pw }) {
  if (!pw) return null;
  const s = pw.length < 6 ? 0 : strength(pw);
  return (
    <div data-testid="password-strength" className="space-y-1">
      <div className="flex gap-1">{[1, 2, 3, 4].map((i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= s ? LEVELS[s][1] : "bg-white/10"}`} />)}</div>
      <p className="text-[11px] text-slate-400" data-testid="password-strength-label">{LEVELS[s][0]}{hint(pw, s)}</p>
    </div>
  );
}

export const homeFor = (u) => (u.role === "admin" ? "/admin" : ["seller", "employee"].includes(u.role) ? "/seller" : u.role === "rider" ? "/rider" : "/");

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);
  const [sp] = useSearchParams();
  const from = useLocation().state?.from;
  useEffect(() => { if (sp.get("replaced")) setErr("You were logged out because your account was logged in on another device."); }, []); // eslint-disable-line
  const doLogin = async (role) => {
    setBusy(true); setErr("");
    try { const u = await login(f.identifier.trim(), f.password, role); toast.success(`Welcome back, ${u.name}!`); nav(u.role === "customer" && from ? from : homeFor(u), { replace: true }); }
    catch (er) {
      const d = er?.response?.data?.detail;
      if (d?.code === "choose_role") setRoles(d.roles); else setErr(errMsg(er));
    } finally { setBusy(false); }
  };
  const submit = (e) => {
    e.preventDefault(); setErr(""); setRoles([]);
    const id = f.identifier.trim();
    if (!id || !f.password) return setErr("Enter your mobile number or email and your password");
    if (!id.includes("@") && !/^(\+?91)?[6-9]\d{9}$/.test(id.replace(/[\s-]/g, ""))) return setErr("Enter a valid 10-digit mobile number, or an email address");
    if (id.includes("@") && !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(id)) return setErr("Enter a valid email address");
    doLogin();
  };
  return (
    <Shell title="Login" sub="Use your mobile number or email">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <Input data-testid="login-email-input" placeholder="Mobile number or email" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} className="h-12" autoComplete="username" />
        <PasswordInput id="login-password-input" autoComplete="current-password" name="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-right text-xs text-[#00f0ff] hover:underline -mt-1">Forgot password?</Link>
        {roles.length > 0 && (
          <div data-testid="login-role-chooser" className="rounded-xl border border-[#00f0ff]/40 bg-[#00f0ff]/5 p-3 space-y-2">
            <p className="text-sm">This number has more than one account. Log in as:</p>
            <div className="flex gap-2">{roles.map((r) => (
              <button type="button" key={r} data-testid={`login-as-${r}`} onClick={() => doLogin(r)} className="flex-1 py-2 rounded-full bg-white text-black text-sm font-semibold capitalize">{r}</button>
            ))}</div>
          </div>
        )}
        {err && <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{err}</p>}
        <button data-testid="login-submit-button" disabled={busy} className={btn}>{busy ? "Signing in..." : "Login"}</button>
      </form>
      <p className="text-sm text-slate-400">New to EAZYSHOPY? <Link to="/register" data-testid="go-register-link" className="text-[#ff5500] font-semibold">Create an account in 30 seconds</Link></p>
    </Shell>
  );
}

const ROLES = [["customer", ShoppingBag, "Customer", "Shop products"], ["seller", Store, "Seller", "Sell on EAZYSHOPY"], ["rider", Bike, "Rider", "Deliver & earn"]];

const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
function bankErrors(f) {
  const e = {}, b = f.bank;
  if (b.holder.trim().length < 2) e.holder = "Enter the account holder name";
  if (!/^\d{9,18}$/.test(b.account_no)) e.account_no = "Account number must be 9 to 18 digits";
  if (b.account_no2 !== b.account_no) e.account_no2 = "Account numbers do not match";
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc)) e.ifsc = "Enter a valid 11-character IFSC";
  if (!f.doc) e.doc = "Upload your passbook front page or bank statement";
  return e;
}

function sellerErrors(f) {
  const e = {}, s = f.shop, b = f.bank;
  if (!f.store_name.trim()) e.store_name = "Store name is required";
  if (!GSTIN_RE.test(f.gstin)) e.gstin = "Enter a valid 15-character GSTIN, e.g. 37AANCD0843L1ZB";
  if (!/^\d{6}$/.test(s.pincode)) e.pincode = "Enter your 6-digit shop PIN code";
  if (s.address.trim().length < 5) e.address = "Enter shop number, building and street";
  if (s.landmark.trim().length < 2) e.landmark = "Add a nearby landmark";
  if (s.city.trim().length < 2) e.city = "City is required";
  if (s.state.trim().length < 2) e.state = "State is required";
  if (b.holder.trim().length < 2) e.holder = "Enter the account holder name";
  if (!/^\d{9,18}$/.test(b.account_no)) e.account_no = "Account number must be 9 to 18 digits";
  if (b.account_no2 !== b.account_no) e.account_no2 = "Account numbers do not match";
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc)) e.ifsc = "Enter a valid 11-character IFSC";
  if (!f.doc) e.doc = "Upload your passbook front page or bank statement";
  return e;
}

const ACC = ["name", "phone", "password", "email", "email_code"];
const BANK = ["holder", "account_no", "account_no2", "ifsc", "doc", "bank_name", "branch"];
const RIDE = ["pincodes", "dl_number", "vehicle_number", "selfie_doc", "dl_doc", "id_doc"];
const STEPS = {
  seller: [["Account", (k) => ACC.includes(k)], ["Business", (k) => ["store_name", "gstin"].includes(k)], ["Shop address", (k) => ![...ACC, ...BANK, "store_name", "gstin"].includes(k)], ["Bank & KYC", (k) => BANK.includes(k)]],
  rider: [["Account", (k) => ACC.includes(k)], ["Vehicle & KYC", (k) => RIDE.includes(k)], ["Bank", (k) => BANK.includes(k)]],
};

function validate(f, otpOn) {
  const e = {};
  if (!/^[A-Za-z][A-Za-z .'-]{1,59}$/.test(f.name.trim())) e.name = "Enter your name using letters only";
  if (!/^[6-9]\d{9}$/.test(f.phone)) e.phone = "Enter a valid 10-digit mobile number";
  if (f.email && !/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(f.email)) e.email = "Enter a valid email (e.g. name@gmail.com) or leave it empty";
  if (f.email && !/^\d{6}$/.test(f.email_code || "")) e.email_code = "Enter the 6-digit code we emailed you (tap Send code)";
  if (f.password.length < 6) e.password = "Password must be at least 6 characters";
  if (f.role === "seller") Object.assign(e, sellerErrors(f));
  if (f.role === "rider") Object.assign(e, riderErrors(f), bankErrors(f));
  if (f.role === "rider" && f.pincodes && !f.pincodes.split(",").every((p) => /^\d{6}$/.test(p.trim()))) e.pincodes = "Use 6-digit pincodes separated by commas";
  if (otpOn && !f.phone_token) e.phone = e.phone || "Verify your mobile number with OTP";
  return e;
}

export function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const init = ["seller", "rider"].includes(sp.get("role")) ? sp.get("role") : "customer";
  useEffect(() => { if (user && ["seller", "rider"].includes(user.role)) nav(`/${user.role}`, { replace: true }); }, [user]); // eslint-disable-line
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "", role: init, store_name: "", vehicle: "Bike", pincodes: "", phone_token: "", gstin: "", doc: null, dl_number: "", vehicle_number: "", id_type: "aadhaar", rdocs: { selfie: null, dl: null, id_proof: null },
    shop: { pincode: "", area: "", address: "", landmark: "", city: "", district: "", state: "", lat: null, lng: null }, bank: { holder: "", account_no: "", account_no2: "", ifsc: "", bank_name: "", branch: "" } });
  const [otpOn, setOtpOn] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const steps = STEPS[f.role] || [["Account", () => true]];
  const last = step >= steps.length - 1;
  const next = () => {
    setTouched(true);
    const all = validate(f, otpOn);
    const mine = Object.fromEntries(Object.entries(all).filter(([k]) => steps[step][1](k)));
    setErrors(mine);
    if (Object.keys(mine).length) return toast.error(Object.values(mine)[0]);
    setTouched(false); setStep(step + 1); window.scrollTo(0, 0);
  };
  const set = (k) => (e) => { const v = k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value; const n = { ...f, [k]: v, ...(k === "phone" ? { phone_token: "" } : {}) }; setF(n); if (touched) setErrors(validate(n, otpOn)); };

  const upd = (patch) => setF((s) => { const n = { ...s, ...(typeof patch === "function" ? patch(s) : patch) }; if (touched) setErrors(validate(n, otpOn)); return n; });
  const submit = async (e) => {
    e.preventDefault();
    if (!last) return next();
    setTouched(true);
    const v = validate(f, otpOn); setErrors(v);
    if (Object.keys(v).length) { const bad = steps.findIndex(([, has]) => Object.keys(v).some(has)); if (bad >= 0 && bad !== step) setStep(bad); return toast.error(Object.values(v)[0]); }
    setBusy(true);
    try {
      const payload = { name: f.name, phone: f.phone, password: f.password, role: f.role, email: f.email || null, email_code: f.email ? f.email_code : "", store_name: f.store_name, vehicle: f.vehicle, phone_token: f.phone_token,
        service_pincodes: f.pincodes.split(",").map((p) => p.trim()).filter(Boolean) };
      if (f.role === "seller") {
        const { account_no2, ...bank } = f.bank;
        Object.assign(payload, { gstin: f.gstin, shop: f.shop, bank, kyc_doc_id: f.doc.id });
      }
      if (f.role === "rider") {
        const { account_no2, ...bank } = f.bank;
        Object.assign(payload, { dl_number: f.dl_number, vehicle_number: f.vehicle_number, id_type: f.id_type, bank, rider_docs: { selfie: f.rdocs.selfie.id, dl: f.rdocs.dl?.id, id_proof: f.rdocs.id_proof.id, passbook: f.doc.id } });
      }
      const u = await register(payload);
      toast.success(u.status === "pending" ? "Registered! Waiting for admin approval." : `Welcome to EAZYSHOPY, ${u.name}!`);
      nav(homeFor(u));
    } catch (er) { toast.error(errMsg(er)); setErrors({ form: errMsg(er) }); } finally { setBusy(false); }
  };

  return (
    <Shell title="Create account" sub={f.role === "seller" ? "Sell on EAZYSHOPY in 4 quick steps" : f.role === "rider" ? "Deliver with EAZYSHOPY in 3 quick steps" : "Just your name, mobile and a password"}>
      {steps.length > 1 && (
        <div data-testid="register-steps" className="flex items-center gap-1.5">
          {steps.map(([n], i) => (
            <button key={n} type="button" data-testid={`register-step-${i}`} disabled={i > step} onClick={() => setStep(i)} className="flex-1 text-left">
              <span className={`block h-1.5 rounded-full ${i <= step ? "bg-[#ff5500]" : "bg-white/10"}`} />
              <span className={`block text-xs mt-1 ${i === step ? "text-white font-semibold" : i < step ? "text-slate-300" : "text-slate-400"}`}>{i + 1}. {n}</span>
            </button>
          ))}
        </div>
      )}
      {step === 0 && <div className="grid grid-cols-3 gap-2" data-testid="register-role-picker">
        {ROLES.map(([r, I, label, sub]) => (
          <button key={r} type="button" data-testid={`register-role-${r}`} onClick={() => { setF({ ...f, role: r }); setStep(0); setErrors({}); }}
            className={`rounded-2xl border p-3 text-left transition-colors ${f.role === r ? "border-[#ff5500] bg-[#ff5500]/10" : "border-white/10 hover:border-white/30"}`}>
            <I size={18} className={f.role === r ? "text-[#ff5500]" : "text-slate-400"} />
            <p className="font-semibold text-sm mt-2">{label}</p><p className="text-[10px] text-slate-500 leading-tight">{sub}</p>
          </button>
        ))}
      </div>}
      <form onSubmit={submit} noValidate className="space-y-3" data-testid="register-form">
        {step === 0 && <>
        <div><Input data-testid="register-name-input" placeholder="Full name" value={f.name} onChange={set("name")} className="h-12" autoComplete="name" /><Err id="register-name-error" msg={errors.name} /></div>
        <div>
          <div className="flex items-center h-12 rounded-md border border-input bg-transparent focus-within:ring-1 focus-within:ring-ring">
            <span className="pl-3 pr-2 text-sm text-slate-400 border-r border-white/10">+91</span>
            <input data-testid="register-phone-input" inputMode="numeric" placeholder="10-digit mobile number" value={f.phone} onChange={set("phone")} className="flex-1 bg-transparent outline-none px-3 text-sm" autoComplete="tel" />
          </div>
          <Err id="register-phone-error" msg={errors.phone} />
          <PhoneOtp phone={f.phone} verified={!!f.phone_token} onEnabled={setOtpOn} onVerified={(t) => setF((s) => ({ ...s, phone_token: t }))} />
        </div>
        <div><PasswordInput id="register-password-input" autoComplete="new-password" name="new-password" value={f.password} onChange={set("password")} placeholder="Create password (min 6 chars)" /><Err id="register-password-error" msg={errors.password} /></div>
        <StrengthMeter pw={f.password} />
        <EmailCode email={f.email} code={f.email_code || ""} setCode={(c) => upd({ email_code: c })} error={errors.email_code}>
          <Input data-testid="register-email-input" type="email" placeholder="Email (for password reset)" value={f.email} onChange={set("email")} className="h-12" /><Err id="register-email-error" msg={errors.email} />
        </EmailCode>
        </>}
        {f.role === "seller" && (
          <>
            {step === 1 && <>
            <div><Input data-testid="register-store-input" placeholder="Store / business name" value={f.store_name} onChange={set("store_name")} className="h-12" /><Err id="register-store-error" msg={errors.store_name} /></div>
            <div><Input data-testid="register-gstin-input" placeholder="GSTIN (15 characters)" value={f.gstin} maxLength={15} onChange={(e) => upd({ gstin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} className="h-12 font-mono" /><Err id="register-gstin-error" msg={errors.gstin} /></div>
            </>}
            {step === 2 && <ShopAddressFields shop={f.shop} setShop={(v) => upd((s) => ({ shop: typeof v === "function" ? v(s.shop) : v }))} errors={errors} />}
            {step === 3 && <BankFields bank={f.bank} setBank={(v) => upd((s) => ({ bank: typeof v === "function" ? v(s.bank) : v }))} doc={f.doc} setDoc={(doc) => upd({ doc })} errors={errors} />}
          </>
        )}
        {f.role === "rider" && step === 1 && (
          <>
            <div className="flex gap-2">{["Bike", "Scooter", "Cycle", "Van"].map((v) => (
              <button key={v} type="button" data-testid={`register-vehicle-${v.toLowerCase()}`} onClick={() => upd({ vehicle: v })} className={`flex-1 py-2 rounded-full text-sm border ${f.vehicle === v ? "border-[#ff5500] bg-[#ff5500]/10" : "border-white/10"}`}>{v}</button>
            ))}</div>
            <div><Input data-testid="register-pincodes-input" placeholder="Delivery pincodes, e.g. 560001, 560002" value={f.pincodes} onChange={set("pincodes")} className="h-12" /><Err id="register-pincodes-error" msg={errors.pincodes} /></div>
            <RiderKycFields f={f} upd={upd} errors={errors} />
          </>
        )}
        {f.role === "rider" && step === 2 && <BankFields bank={f.bank} setBank={(v) => upd((s) => ({ bank: typeof v === "function" ? v(s.bank) : v }))} doc={f.doc} setDoc={(doc) => upd({ doc })} errors={errors} />}
        {f.role !== "customer" && <p className="text-xs text-slate-500">{f.role === "seller" ? "Seller" : "Rider"} accounts are activated after admin approval.</p>}
        <div className="flex gap-2">
          {step > 0 && <button type="button" data-testid="register-back-button" onClick={() => setStep(step - 1)} className="px-6 py-3 rounded-full border border-white/15 font-semibold">Back</button>}
          <button data-testid="register-submit-button" disabled={busy} className={btn}>{busy ? "Creating your account..." : !last ? `Next: ${steps[step + 1][0]}` : f.role === "customer" ? "Create account" : `Register as ${f.role}`}</button>
        </div>
      </form>
      <p className="text-sm text-slate-400">Already have an account? <Link to="/login" data-testid="go-login-link" className="text-[#ff5500] font-semibold">Login</Link></p>
    </Shell>
  );
}
